import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const WHATSAPP_URL =
  'https://wa.me/5585982245030?text=Olá!%20Vim%20pelo%20site%20da%20Naturalle%20Sanduíches%20e%20gostaria%20de%20mais%20informações.';

const HeroSection = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            section.querySelectorAll('.reveal').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const trust = ['100% Natural', 'Selado a vácuo', 'Entrega sem taxa'];

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="relative min-h-screen flex items-center overflow-hidden bg-background-50 pt-24 md:pt-28"
    >
      {/* Background image + dark overlays */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Moody%20dark%20editorial%20food%20background%20of%20fresh%20natural%20sandwich%20ingredients%20scattered%20across%20a%20deep%20charcoal%20slate%20surface%2C%20dramatic%20low-key%20studio%20lighting%20from%20the%20side%2C%20deep%20olive%20green%20and%20soft%20terracotta%20tones%2C%20rich%20dark%20shadows%2C%20generous%20dark%20negative%20space%20on%20the%20left%20for%20text%20overlay%2C%20luxurious%20cinematic%20composition%2C%20abstract%20and%20elegant&width=1920&height=1080&seq=hero-bg-naturalle-premium&orientation=landscape"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/85 to-background-50/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/70" />
      </div>

      {/* Warm glows */}
      <div className="absolute top-1/3 right-[8%] w-[420px] h-[420px] rounded-full bg-secondary-500/10 blur-[120px]" />
      <div className="absolute bottom-0 left-[-6%] w-[380px] h-[380px] rounded-full bg-accent-500/10 blur-[120px]" />

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 py-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-12 items-center">
          {/* Left content */}
          <div className="order-2 lg:order-1">
            <span className="reveal reveal-delay-1 inline-flex items-center gap-3 font-label text-[11px] md:text-xs tracking-[0.3em] uppercase text-secondary-500 mb-7">
              <span className="w-8 h-px bg-secondary-500/60" />
              Desde 2002 · Fortaleza, CE
            </span>

            <h1 className="reveal reveal-delay-2 font-display text-[46px] leading-[1.02] sm:text-6xl md:text-7xl lg:text-[86px] text-foreground-950 mb-7">
              Sanduíches
              <br />
              naturais com
              <br />
              <span className="italic text-accent-400">sabor de verdade</span>
            </h1>

            <p className="reveal reveal-delay-3 font-body text-base md:text-lg text-foreground-400 leading-relaxed max-w-lg mb-9">
              Produzidos diariamente com ingredientes frescos, selados a vácuo e prontos para o seu dia.
              Tradição, qualidade e praticidade em cada mordida.
            </p>

            <div className="reveal reveal-delay-4 flex flex-wrap gap-3 md:gap-4 mb-12">
              <Link
                to="/cardapio"
                className="inline-flex items-center gap-2 font-label text-sm px-8 py-4 rounded-full bg-accent-500 text-white hover:bg-accent-400 transition-all duration-300 whitespace-nowrap"
              >
                <span>Explorar o cardápio</span>
                <i className="ri-arrow-right-line" />
              </Link>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-label text-sm px-8 py-4 rounded-full border border-secondary-500/40 text-foreground-700 hover:border-secondary-500 hover:text-secondary-400 transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-secondary-500" />
                <span>Fale no WhatsApp</span>
              </a>
            </div>

            {/* Trust row */}
            <div className="reveal reveal-delay-5 flex flex-wrap items-center gap-x-8 gap-y-3">
              {trust.map((item) => (
                <span key={item} className="inline-flex items-center gap-2 font-label text-xs text-foreground-400">
                  <i className="ri-checkbox-circle-line text-secondary-500" />
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* Right image */}
          <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
            <div className="reveal reveal-delay-2 relative w-[260px] sm:w-[340px] md:w-[420px] lg:w-[460px]">
              {/* Gold frame corners */}
              <div className="absolute -top-3 -left-3 w-16 h-16 border-t border-l border-secondary-500/50 rounded-tl-3xl" />
              <div className="absolute -bottom-3 -right-3 w-16 h-16 border-b border-r border-secondary-500/50 rounded-br-3xl" />

              <div className="relative rounded-[2rem] overflow-hidden border border-secondary-500/20 aspect-[4/5]">
                <img
                  src="https://storage.helloreaddy.io/project_files/46b11d92-1101-4f92-aa67-f7a4033739d8/f110ce81-23bf-4185-92f2-cca6782f5bd4_compressed_Captura-de-tela-2026-07-24-145831.webp"
                  alt="Sanduíche natural Naturalle"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-50/60 via-transparent to-transparent" />
              </div>

              {/* Floating badge */}
              <div className="glass-warm absolute -left-4 md:-left-8 bottom-10 rounded-2xl px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-secondary-500/15 flex items-center justify-center shrink-0">
                    <i className="ri-leaf-line text-secondary-500" />
                  </div>
                  <div>
                    <div className="font-heading text-xs font-semibold text-foreground-900 whitespace-nowrap">
                      100% Natural
                    </div>
                    <div className="font-label text-[10px] text-foreground-500 whitespace-nowrap">
                      Ingredientes frescos
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 z-10">
        <span className="font-label text-[10px] tracking-[0.3em] uppercase text-foreground-500">Role</span>
        <div className="w-6 h-10 rounded-full border border-secondary-500/30 flex justify-center pt-2">
          <span className="w-1 h-2 rounded-full bg-secondary-500 animate-scroll-dot" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;